import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Save,
  Upload,
  X,
  Loader2,
  Phone,
  Mail,
  MapPin,
  Globe,
  Calendar,
  Users,
  GraduationCap,
  Image as ImageIcon,
  Video,
  Facebook,
  Instagram,
  Twitter
} from 'lucide-react';
import { admissionsService } from '../../../firebase/admissions';
import { useRealtime } from '../../../contexts/RealtimeContext';
import toast from 'react-hot-toast';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { COLLECTIONS } from '../../../firebase/collections';
import R2Image from '../../../components/R2Image';

const AdmissionsProfile = () => {
  const { currentUser, userProfile } = useRealtime();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [institutionData, setInstitutionData] = useState({
    name: '',
    type: 'coaching',
    description: '',
    applicationFee: 500,
    profilePicture: '',
    address: {
      street: '',
      city: '',
      state: '',
      pincode: ''
    },
    contact: {
      phone: '',
      email: '',
      website: ''
    },
    socialMedia: {
      facebook: '',
      instagram: '',
      twitter: ''
    },
    established: '',
    students: '',
    virtualTourVideo: '',
    isActive: true
  });

  useEffect(() => {
    loadInstitution();
  }, []);

  const loadInstitution = async () => {
    try {
      setLoading(true);
      const institutionId = userProfile?.coachingId || userProfile?.institutionId;
      
      if (!institutionId) {
        // Initialize with user profile data
        setInstitutionData({
          name: userProfile?.coachingName || '',
          type: 'coaching',
          description: '',
          applicationFee: 500,
          address: userProfile?.address || { street: '', city: '', state: '', pincode: '' },
          contact: {
            phone: userProfile?.phone || '',
            email: userProfile?.email || currentUser?.email || '',
            website: ''
          },
          socialMedia: {
            facebook: '',
            instagram: '',
            twitter: ''
          },
          established: '',
          students: '',
          profilePicture: '',
          virtualTourVideo: '',
          isActive: true
        });
        setLoading(false);
        return;
      }

      const result = await admissionsService.getInstitutionById(institutionId);
      if (result.success) {
        setInstitutionData({
          name: result.institution.name || userProfile?.coachingName || '',
          type: result.institution.type || 'coaching',
          description: result.institution.description || '',
          applicationFee: result.institution.applicationFee || 500,
          profilePicture: result.institution.profilePicture || '',
          address: result.institution.address || { street: '', city: '', state: '', pincode: '' },
          contact: result.institution.contact || {
            phone: userProfile?.phone || '',
            email: userProfile?.email || currentUser?.email || '',
            website: ''
          },
          socialMedia: result.institution.socialMedia || {
            facebook: '',
            instagram: '',
            twitter: ''
          },
          established: result.institution.established || '',
          students: result.institution.students || '',
          virtualTourVideo: result.institution.virtualTourVideo || '',
          isActive: result.institution.isActive !== undefined ? result.institution.isActive : true
        });
      }
    } catch (error) {
      toast.error('Error loading institution data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setInstitutionData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setInstitutionData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleProfilePictureUpload = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingPhoto(true);
      const institutionId = userProfile?.coachingId || userProfile?.institutionId;
      if (!institutionId) {
        toast.error('Please save your profile first');
        return;
      }

      const result = await admissionsService.uploadInstitutionImage(file, institutionId);
      if (result.success) {
        setInstitutionData(prev => ({
          ...prev,
          profilePicture: result.url
        }));
        toast.success('Profile picture uploaded successfully');
      } else {
        toast.error('Failed to upload profile picture: ' + result.error);
      }
    } catch (error) {
      toast.error('Error uploading profile picture: ' + error.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!institutionData.name || institutionData.name.trim() === '') {
      toast.error('Please enter institution name');
      return;
    }

    if (!institutionData.type) {
      toast.error('Please select institution type');
      return;
    }

    try {
      setSaving(true);
      const coachingId = userProfile?.coachingId;
      const existingInstitutionId = userProfile?.institutionId;
      
      // Use coachingId as institutionId if available, otherwise use existing institutionId
      // The service will check if it exists and create if it doesn't
      const institutionId = coachingId || existingInstitutionId;

      // Prepare data - ensure all required fields are present
      const dataToSave = {
        name: institutionData.name.trim(),
        type: institutionData.type,
        description: institutionData.description || '',
        applicationFee: institutionData.applicationFee || 0,
        address: institutionData.address || {
          street: '',
          city: '',
          state: '',
          pincode: ''
        },
        contact: institutionData.contact || {
          phone: '',
          email: '',
          website: ''
        },
        socialMedia: institutionData.socialMedia || {
          facebook: '',
          instagram: '',
          twitter: ''
        },
        established: institutionData.established || '',
        students: institutionData.students || '',
        profilePicture: institutionData.profilePicture || '',
        virtualTourVideo: institutionData.virtualTourVideo || '',
        // Ensure isActive is always true for new institutions to appear in listings
        isActive: true
      };

      // Add coachingId if available
      if (coachingId) {
        dataToSave.coachingId = coachingId;
      }

      // The service will handle checking if document exists and create/update accordingly
      const result = institutionId
        ? await admissionsService.saveInstitution(dataToSave, institutionId)
        : await admissionsService.saveInstitution(dataToSave);

      if (result.success) {
        toast.success('Profile saved successfully');
        
        // If new institution was created, update user profile with institutionId
        const newInstitutionId = result.institutionId;
        if (!existingInstitutionId && newInstitutionId) {
          try {
            await updateDoc(doc(db, COLLECTIONS.USERS, currentUser.uid), {
              institutionId: newInstitutionId,
              updatedAt: new Date()
            });
            toast.success('Profile linked successfully');
            // Reload to get updated profile
            setTimeout(() => window.location.reload(), 1000);
          } catch (updateError) {
            toast.error('Profile saved but failed to link. Please refresh.');
          }
        }
      } else {
        const errorMsg = result.error || 'Unknown error';
        toast.error(`Failed to save profile: ${errorMsg}`);
      }
    } catch (error) {
      toast.error(`Error saving profile: ${error.message || 'Please try again'}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Institution Profile</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Manage your institution's basic information and contact details
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 space-y-6">
        {/* Basic Information */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h2>
          <div className="space-y-4">
            {/* Profile Picture Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Profile Picture
              </label>
              <div className="flex items-center gap-4">
                {institutionData.profilePicture ? (
                  <div className="relative">
                    <R2Image
                      fileKey={institutionData.profilePicture}
                      alt="Profile"
                      className="w-32 h-32 object-cover rounded-lg border-2 border-gray-300 dark:border-gray-600"
                    />
                    <button
                      onClick={() => handleInputChange('profilePicture', '')}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      title="Remove picture"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-32 h-32 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600">
                    <ImageIcon className="w-12 h-12 text-gray-400" />
                  </div>
                )}
                <div className="flex-1">
                  <label className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${
                    uploadingPhoto ? 'opacity-50 cursor-not-allowed' : 'border-gray-300 dark:border-gray-600'
                  }`}>
                    {uploadingPhoto ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5 text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {institutionData.profilePicture ? 'Change Picture' : 'Upload Profile Picture'}
                        </span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          handleProfilePictureUpload(e.target.files[0]);
                        }
                      }}
                      disabled={uploadingPhoto}
                    />
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Recommended: Square image, max 5MB (JPG, PNG)
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Institution Name *
              </label>
              <input
                type="text"
                value={institutionData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Type *
              </label>
              <select
                value={institutionData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="school">School</option>
                <option value="college">College</option>
                <option value="coaching">Coaching</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={institutionData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <div className="flex items-center gap-2">
                  <Video className="w-4 h-4" />
                  Virtual Tour Video URL
                </div>
              </label>
              <input
                type="url"
                value={institutionData.virtualTourVideo}
                onChange={(e) => handleInputChange('virtualTourVideo', e.target.value)}
                placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Simply paste any YouTube video URL (watch or youtu.be format) - no embedding needed
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Application Fee (₹)
                </label>
                <input
                  type="number"
                  value={institutionData.applicationFee}
                  onChange={(e) => handleInputChange('applicationFee', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Established Year
                </label>
                <input
                  type="text"
                  value={institutionData.established}
                  onChange={(e) => handleInputChange('established', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Contact Information</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={institutionData.contact.phone}
                  onChange={(e) => handleInputChange('contact.phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={institutionData.contact.email}
                  onChange={(e) => handleInputChange('contact.email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Website
              </label>
              <input
                type="url"
                value={institutionData.contact.website}
                onChange={(e) => handleInputChange('contact.website', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        </div>

        {/* Address */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Address</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Street Address
              </label>
              <input
                type="text"
                value={institutionData.address.street}
                onChange={(e) => handleInputChange('address.street', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  City
                </label>
                <input
                  type="text"
                  value={institutionData.address.city}
                  onChange={(e) => handleInputChange('address.city', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  State
                </label>
                <input
                  type="text"
                  value={institutionData.address.state}
                  onChange={(e) => handleInputChange('address.state', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Pincode
                </label>
                <input
                  type="text"
                  value={institutionData.address.pincode}
                  onChange={(e) => handleInputChange('address.pincode', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Social Media</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <div className="flex items-center gap-2">
                  <Facebook className="w-4 h-4 text-blue-600" />
                  Facebook URL
                </div>
              </label>
              <input
                type="url"
                value={institutionData.socialMedia?.facebook || ''}
                onChange={(e) => handleInputChange('socialMedia.facebook', e.target.value)}
                placeholder="https://www.facebook.com/..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <div className="flex items-center gap-2">
                  <Instagram className="w-4 h-4 text-pink-600" />
                  Instagram URL
                </div>
              </label>
              <input
                type="url"
                value={institutionData.socialMedia?.instagram || ''}
                onChange={(e) => handleInputChange('socialMedia.instagram', e.target.value)}
                placeholder="https://www.instagram.com/..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <div className="flex items-center gap-2">
                  <Twitter className="w-4 h-4 text-sky-500" />
                  Twitter URL
                </div>
              </label>
              <input
                type="url"
                value={institutionData.socialMedia?.twitter || ''}
                onChange={(e) => handleInputChange('socialMedia.twitter', e.target.value)}
                placeholder="https://www.twitter.com/... or https://x.com/..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        </div>

        {/* Visibility Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Visibility Settings</h2>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={institutionData.isActive}
              onChange={(e) => handleInputChange('isActive', e.target.checked)}
              className="w-5 h-5"
            />
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Show on Admissions Page
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                When enabled, your institution will be visible to students browsing admissions
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Save Profile
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdmissionsProfile;

